// Main Game singleton - orchestrates all game systems
import * as THREE from 'three';
import { Time } from './Time';
import { Resources } from './Resources';
import { Rendering } from './Rendering';
import { View } from './View';
import { World } from './World/World';
import { UI } from './UI';
import { Audio } from './Audio';
import { gameState } from '../game/gameState';
import { ExperienceConfig } from '../shared/types';

export class Game {
    private static instance: Game;

    public time!: Time;
    public resources!: Resources;
    public rendering!: Rendering;
    public view!: View;
    public world!: World;
    public ui!: UI;
    public audio!: Audio;

    private container: HTMLElement;
    private isRunning: boolean = false;
    private animationId: number = 0;
    private config?: ExperienceConfig;

    private constructor(container: HTMLElement, config?: ExperienceConfig) {
        this.container = container;
        this.config = config;
        this.init();
    }

    static create(container: HTMLElement, config?: ExperienceConfig): Game {
        if (!Game.instance) {
            Game.instance = new Game(container, config);
        }
        return Game.instance;
    }

    static getInstance(): Game {
        return Game.instance;
    }

    private async init(): Promise<void> {
        console.log('🎮 Initializing Valentine Town Game...');
        if (this.config) {
            console.log('✨ Applying Custom Experience Config:', this.config.subdomain);
            gameState.setExperienceConfig(this.config);
        }

        // Initialize core systems
        this.time = Time.getInstance();
        this.resources = Resources.getInstance();
        this.rendering = Rendering.init(this.container);
        this.view = View.init(this.container);

        // Setup lighting
        this.setupLighting();

        // Create world
        this.world = new World();

        // Initialize audio
        this.audio = Audio.getInstance();

        // Check for share token FIRST
        const params = new URLSearchParams(window.location.search);
        const shareToken = params.get('share');

        if (shareToken) {
            console.log('💌 Found share token, initializing view-only mode...');
            try {
                // Ensure we await this fully before creating UI
                await gameState.initFromShareToken(shareToken);
                console.log('✅ View-only mode initialized successfully');
            } catch (error) {
                console.error('❌ Failed to initialize from share token:', error);
                // Fallback to noraml init if share fails? 
                // For now, let's just log it. The UI will default to intro if state isn't view-only.
            }
        } else {
            // Normal Session Init if needed? 
            // Currently UI handles session init after intro.
        }

        // Initialize UI overlay (after game state is set)
        // This must happen AFTER await gameState.initFromShareToken
        this.ui = UI.init(this.container);

        // Setup post-processing
        this.rendering.setupPostProcessing(this.view.camera);

        // Load initial assets
        await this.loadAssets();

        // Subscribe to game state changes
        gameState.subscribe(async (state) => {
            await this.world.updateFromState(state.townState);
        });

        // Handle resize
        window.addEventListener('resize', () => this.onResize());

        // Start the render loop
        this.start();

        console.log('✅ Game initialized!');
    }

    private setupLighting(): void {
        const scene = this.rendering.scene;

        // Ambient light for base illumination - Balanced
        const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
        scene.add(ambientLight);

        // Directional light (sun)
        // Keep front-left position for face visibility, but reduce intensity
        const directionalLight = new THREE.DirectionalLight(0xfff5e6, 1.0);
        directionalLight.position.set(-10, 30, 30); // Front-left-ish lighting
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.width = 2048;
        directionalLight.shadow.mapSize.height = 2048;
        directionalLight.shadow.camera.near = 0.5;
        directionalLight.shadow.camera.far = 150;
        directionalLight.shadow.camera.left = -50;
        directionalLight.shadow.camera.right = 50;
        directionalLight.shadow.camera.top = 50;
        directionalLight.shadow.camera.bottom = -50;
        directionalLight.shadow.bias = -0.0001;
        scene.add(directionalLight);

        // Weak fill light from the other side to soften shadows
        const fillLight = new THREE.DirectionalLight(0xE6E6FA, 0.5);
        fillLight.position.set(20, 20, 20);
        scene.add(fillLight);

        // Hemisphere light for natural sky/ground gradient
        const hemisphereLight = new THREE.HemisphereLight(0x87CEEB, 0x444444, 0.8);
        scene.add(hemisphereLight);
    }

    private async loadAssets(): Promise<void> {
        // Assets are now loaded dynamically or are procedural
        // Keeping this method stub for future use if needed
        return Promise.resolve();
    }

    private start(): void {
        if (this.isRunning) return;
        this.isRunning = true;
        this.tick();
    }

    private tick(): void {
        if (!this.isRunning) return;

        // Update time
        this.time.update();

        // Update view/camera
        this.view.update();

        // Update world
        this.world.update(this.time.delta);

        // Render
        this.rendering.render(this.view.camera);

        // Next frame
        this.animationId = requestAnimationFrame(() => this.tick());
    }

    private onResize(): void {
        this.rendering.resize();
        this.view.resize(this.container);
    }

    stop(): void {
        this.isRunning = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
        }
    }
}
